/**
 * Shared TaskDto → TaskRowModel adapter for list screens.
 *
 * Date labels follow the prototype's behaviour: pick the most relevant
 * timestamp for the task's status (review for waiting/delegated; due
 * otherwise; scheduled as fallback). Date state is `'overdue' | 'today' |
 * 'normal'` based on which window the timestamp falls into.
 *
 * If three or more screens want a different shape (waiting-since label,
 * relative-time label, etc.), extend the options bag rather than forking
 * the helper.
 */

import type { TaskRowModel } from '@k-os/ui';
import type { TaskDto } from '../api/tasks';

export interface ToRowOptions {
  /** When true, derive a `waiting <Nd>` label from `createdAt` for the meta row. */
  showWaitingSince?: boolean;
  /** When true, derive an `updated <Nd>d ago` label from `updatedAt` for the meta row. */
  showUpdatedSince?: boolean;
}

export function toRowModel(t: TaskDto, opts: ToRowOptions = {}): TaskRowModel {
  return {
    id: t.id,
    title: t.title,
    status: t.status,
    priority: t.priority,
    done: t.status === 'done',
    context: t.context ?? null,
    // Fall back to area name when the task is filed under an area rather
    // than a project (the design's "proj chip" reads either as the parent).
    projectName: t.project?.name ?? t.area?.name ?? null,
    person: t.person
      ? { name: t.person.name, initials: t.person.initials, color: t.person.color }
      : null,
    dateLabel: chooseDateLabel(t),
    dateState: chooseDateState(t),
    waitingSince: opts.showWaitingSince ? formatWaitingSince(t.createdAt) : null,
    metaNote: opts.showUpdatedSince ? formatUpdatedSince(t.updatedAt) : null,
  };
}

function chooseDateLabel(t: TaskDto): string | null {
  // Once a task is done its `dueAt` is in the past — but we want the Closed
  // group on Today to surface *when it was completed*, not a stale due date.
  if (t.status === 'done' && t.completedAt) {
    return formatShortDate(new Date(t.completedAt));
  }
  if (t.status === 'waiting' || t.status === 'delegated') {
    return t.reviewAt ? formatShortDate(new Date(t.reviewAt)) : null;
  }
  // Always surface the DUE date (never the scheduled date) so the date column
  // is consistent across every list — including the Scheduled section, where
  // a scheduled task with no due date simply shows no date chip.
  if (t.dueAt) return formatShortDate(new Date(t.dueAt));
  return null;
}

function chooseDateState(t: TaskDto): 'overdue' | 'today' | 'normal' {
  // A completed task is never "overdue" — its due date is irrelevant now.
  if (t.status === 'done') return 'normal';
  // Overdue/today is driven ONLY by the due date — a past *scheduled* (or
  // review) date must NOT paint the row's red overdue stripe. No due date ⇒
  // never overdue.
  if (!t.dueAt) return 'normal';
  const ms = Date.parse(t.dueAt);
  const start = startOfToday();
  const end = endOfToday();
  if (ms < start.getTime()) return 'overdue';
  if (ms <= end.getTime()) return 'today';
  return 'normal';
}

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
export function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

export function formatShortDate(d: Date): string {
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatWaitingSince(iso: string): string {
  const days = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'today';
  return `${days}d`;
}

function formatUpdatedSince(iso: string): string {
  const days = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'updated today';
  return `updated ${days}d ago`;
}
